import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

interface UseAssistantUrlChatSelectionProps {
  queryParam: string
  queryParamAliases?: Array<string>
}

interface LocationParts {
  hash: string
  pathname: string
  search: string
}

interface ChatSelection {
  chatId: number | undefined
  shouldClearUrl: boolean
}

const DEFAULT_QUERY_PARAM_ALIASES: Array<string> = []

function parseChatId(rawChatId: string | null): number | undefined {
  if (!rawChatId) {
    return undefined
  }

  const chatId = Number(rawChatId)
  return Number.isSafeInteger(chatId) && chatId > 0 ? chatId : undefined
}

function getQueryParams(queryParam: string, queryParamAliases: Array<string>): Array<string> {
  return [queryParam, ...queryParamAliases]
}

function buildLocationWithChatId(
  queryParam: string,
  queryParamAliases: Array<string>,
  chatId: number | undefined,
  location: LocationParts,
): LocationParts {
  const searchParams = new URLSearchParams(location.search)

  for (const param of getQueryParams(queryParam, queryParamAliases)) {
    searchParams.delete(param)
  }

  if (chatId !== undefined) {
    searchParams.set(queryParam, String(chatId))
  }

  const nextSearch = searchParams.toString()
  return {
    hash: location.hash,
    pathname: location.pathname,
    search: nextSearch ? `?${nextSearch}` : '',
  }
}

function readChatSelection(queryParam: string, queryParamAliases: Array<string>, search: string): ChatSelection {
  const searchParams = new URLSearchParams(search)

  for (const param of getQueryParams(queryParam, queryParamAliases)) {
    if (!searchParams.has(param)) {
      continue
    }

    const chatId = parseChatId(searchParams.get(param))
    return {
      chatId,
      shouldClearUrl: chatId === undefined,
    }
  }

  return {
    chatId: undefined,
    shouldClearUrl: false,
  }
}

export function useAssistantUrlChatSelection({
  queryParam,
  queryParamAliases = DEFAULT_QUERY_PARAM_ALIASES,
}: UseAssistantUrlChatSelectionProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const queryParams = useMemo(() => getQueryParams(queryParam, queryParamAliases), [queryParam, queryParamAliases])
  const currentSelection = useMemo(
    () => readChatSelection(queryParam, queryParamAliases, location.search),
    [location.search, queryParam, queryParamAliases],
  )
  const previousPathname = useRef(location.pathname)
  const [selectedChatId, setSelectedChatId] = useState(currentSelection.chatId)

  const replaceCurrentUrlChatId = useCallback((chatId: number | undefined) => {
    const nextLocation = buildLocationWithChatId(queryParam, queryParamAliases, chatId, location)
    if (
      nextLocation.hash !== location.hash ||
      nextLocation.pathname !== location.pathname ||
      nextLocation.search !== location.search
    ) {
      navigate(nextLocation, { replace: true, state: location.state })
    }
  }, [location, navigate, queryParam, queryParamAliases])

  useEffect(() => {
    const pathnameChanged = previousPathname.current !== location.pathname
    previousPathname.current = location.pathname

    if (currentSelection.shouldClearUrl) {
      replaceCurrentUrlChatId(undefined)
      setSelectedChatId(undefined)
      return
    }

    if (currentSelection.chatId === undefined && !pathnameChanged) {
      return
    }

    setSelectedChatId(previous => previous === currentSelection.chatId ? previous : currentSelection.chatId)
  }, [currentSelection, location.pathname, replaceCurrentUrlChatId])

  const selectChat = useCallback((chatId: number) => {
    setSelectedChatId(chatId)
    replaceCurrentUrlChatId(chatId)
  }, [replaceCurrentUrlChatId])

  const clearChatSelection = useCallback(() => {
    setSelectedChatId(undefined)
    replaceCurrentUrlChatId(undefined)
  }, [replaceCurrentUrlChatId])

  const removeChatIdFromUrl = useCallback(() => {
    const searchParams = new URLSearchParams(location.search)
    if (!queryParams.some(param => searchParams.has(param))) {
      return
    }

    replaceCurrentUrlChatId(undefined)
  }, [location.search, queryParams, replaceCurrentUrlChatId])

  return {
    selectedChatId,
    urlChatId: currentSelection.chatId,
    selectChat,
    clearChatSelection,
    removeChatIdFromUrl,
  }
}
